import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { CONTACT_EMAIL, NAV, ease } from "../lib/content";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const solid = scrolled && !open;

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,color,border-color,padding] duration-500 ${
          solid ? "border-b border-teal/10 bg-paper/80 py-3 text-teal backdrop-blur-xl" : "border-b border-transparent py-5 text-mist"
        }`}
      >
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 sm:px-8 lg:px-12">
          <Link to="/" data-testid="header-logo-link" aria-label="Bio Smart Solutions home">
            <Logo />
          </Link>
          <nav className="hidden items-center gap-9 lg:flex">
            {NAV.slice(0, 4).map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `label-caps link-underline pb-1 !text-[11px] ${isActive ? "bg-[length:100%_1px]" : ""}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/contact"
              data-testid="header-contact-btn"
              className={`group hidden items-center gap-2 rounded-full border px-5 py-2.5 text-[11px] transition-colors duration-300 sm:inline-flex label-caps ${
                solid ? "border-teal bg-teal text-mist hover:bg-teal-ink" : "border-mist/40 hover:bg-mist hover:text-teal"
              }`}
            >
              Get in touch
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-45" />
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((o) => !o)}
              className="flex h-11 w-11 items-center justify-center rounded-full border border-current/30 lg:hidden"
              aria-label="Toggle menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 flex flex-col justify-between bg-teal px-6 pb-10 pt-32 text-mist lg:hidden"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.7, ease }}
          >
            <nav className="flex flex-col gap-2">
              {[{ label: "Home", to: "/" }, ...NAV].map((n, i) => (
                <motion.div key={n.to} initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.15 + i * 0.06, ease, duration: 0.7 }}>
                  <Link to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase()}`} className="block font-display text-5xl font-light tracking-tight">
                    {n.label}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <a href={`mailto:${CONTACT_EMAIL}`} className="label-caps text-mist/80">
              {CONTACT_EMAIL}
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
